import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { currencies, languages } from '@/types';
import { useLocationCurrency } from '@/hooks/useLocationCurrency';

export default function OrganizationCreate() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, addOrganization, setCurrentOrganization } = useAppStore();
  const { currency: detectedCurrency, loading: currencyLoading } = useLocationCurrency('EUR');
  
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState('EUR');

  useEffect(() => {
    if (!currencyLoading) {
      setCurrency(detectedCurrency);
    }
  }, [detectedCurrency, currencyLoading]);

  const [language, setLanguage] = useState(i18n.language as 'en' | 'fr' | 'es');
  const [monthlyDues, setMonthlyDues] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('Organization name is required');
      return;
    }

    if (!user) {
      toast.error('You must be logged in');
      return;
    }

    setIsLoading(true);

    try {
      // Generate a unique join code for the organization
      const joinCode = Math.random().toString(36).substring(2, 10).toUpperCase();

      // Create organization
      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: name.trim(),
          currency,
          language,
          monthly_dues_amount: parseFloat(monthlyDues) || 0,
          join_code: joinCode,
          address: address || null,
          phone: phone || null,
          email: email || null,
          created_by: user.id,
        })
        .select()
        .single();

      if (orgError) throw orgError;

      // Create user_organization junction with admin role
      const { data: junctionData, error: junctionError } = await supabase
        .from('user_organizations')
        .insert({
          user_id: user.id,
          organization_id: orgData.id,
          role: 'admin',
          is_active: true,
        })
        .select(`
          *,
          organization:organizations(*)
        `)
        .single();

      if (junctionError) throw junctionError;

      // Update app state
      addOrganization(junctionData);
      setCurrentOrganization(orgData, 'admin');

      toast.success(t('organization.createSuccess'));
      navigate('/dashboard');
    } catch (error) {
      console.error('Error creating organization:', error);
      toast.error(error instanceof Error ? error.message : t('errors.generic'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Button
        variant="ghost"
        className="mb-4"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
              <Building2 className="w-5 h-5 text-primary" />
            </div>
            <div>
              <CardTitle>{t('organization.create')}</CardTitle>
              <CardDescription>
                Set up your organization details
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name">{t('organization.name')} *</Label>
              <Input
                id="name"
                placeholder="e.g., St. Mary's Church"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="currency">{t('organization.currency')}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} - {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language">{t('organization.language')}</Label>
                <Select value={language} onValueChange={(v) => setLanguage(v as 'en' | 'fr' | 'es')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {languages.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="monthlyDues">{t('organization.monthlyDues')}</Label>
              <Input
                id="monthlyDues"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
                value={monthlyDues}
                onChange={(e) => setMonthlyDues(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">{t('organization.address')} (Optional)</Label>
              <Input
                id="address"
                placeholder="Organization address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">{t('organization.phone')} (Optional)</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="+1234567890"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('organization.email')} (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="org@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('app.loading')}
                </>
              ) : (
                t('organization.create')
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
